import React, { useEffect, useId, useMemo, useRef, useState } from "react";
import { brand } from "../theme/brand";
import { isNative, isRealMobilePhone } from "../lib/platform";
import { resetPassword, signIn, signInWithMagicLink, signOut, signUp, updatePassword } from "./auth";
import { signInWithApple, signInWithGoogle } from "./socialAuth";
import { clearCompletedPasswordRecoveryEmail } from "./passwordRecoveryState";

type AuthModalMode = "signin" | "signup" | "reset" | "updatePassword";
type SignInStep = "identify" | "authenticate" | "magicSent";
type AuthMethod = "magic" | "password";

type Props = {
  onClose: () => void;
  initialMode?: "signin" | "signup" | "updatePassword";
  initialBanner?: Banner | null;
  initialEmail?: string | null;
  onPasswordUpdateSuccess?: (email: string | null) => Promise<void> | void;
};

type Banner = {
  variant: "error" | "info" | "success";
  title: string;
  message: string;
};

const LAST_EMAIL_KEY = "wp:lastSignInEmail";
const RESEND_SECONDS = 30;

const overlayStyle: React.CSSProperties = {
  position: "fixed",
  inset: 0,
  background: "rgba(0,0,0,0.65)",
  display: "flex",
  // Top-anchored so the overlay itself can scroll when the keyboard squeezes
  // the available height; `margin: auto` on the panel still keeps it visually
  // centered when there is room.
  alignItems: "flex-start",
  justifyContent: "center",
  zIndex: 30010,
  overflowY: "auto",
  overscrollBehavior: "contain",
  WebkitOverflowScrolling: "touch",
  padding: "calc(env(safe-area-inset-top) + 24px) 16px calc(env(safe-area-inset-bottom) + 24px)",
};

const panelStyle: React.CSSProperties = {
  background: brand.background.card,
  border: "1px solid rgba(148,163,184,0.2)",
  borderRadius: 18,
  padding: "2rem",
  width: "min(400px, calc(100vw - 2rem))",
  display: "grid",
  gap: "1rem",
  marginTop: "auto",
  marginBottom: "auto",
};

const inputStyle: React.CSSProperties = {
  width: "100%",
  background: "rgba(255,255,255,0.05)",
  border: "1px solid rgba(255,255,255,0.18)",
  borderRadius: 10,
  color: "white",
  padding: "0.65rem 0.75rem",
  fontSize: "0.95rem",
  boxSizing: "border-box",
};

const inputErrorStyle: React.CSSProperties = {
  ...inputStyle,
  borderColor: "rgba(239,68,68,0.6)",
};

const btnPrimary: React.CSSProperties = {
  width: "100%",
  background: brand.accent.brand,
  border: `1px solid rgba(132,204,22,0.5)`,
  borderRadius: 10,
  color: "white",
  fontWeight: 700,
  fontSize: "0.97rem",
  padding: "0.7rem",
  cursor: "pointer",
  minHeight: "2.65rem",
};

const btnGhost: React.CSSProperties = {
  background: "none",
  border: "none",
  color: "rgba(148,163,184,0.8)",
  fontSize: "0.85rem",
  cursor: "pointer",
  textDecoration: "underline",
  padding: 0,
};

const btnApple: React.CSSProperties = {
  width: "100%",
  background: "#000000",
  border: "1px solid #000000",
  borderRadius: 10,
  color: "#ffffff",
  fontWeight: 600,
  fontSize: "0.97rem",
  padding: "0.7rem",
  cursor: "pointer",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  gap: "0.5rem",
};

const AppleIcon = () => (
  <svg width="16" height="18" viewBox="0 0 14 17" aria-hidden="true" fill="#ffffff">
    <path d="M11.62 8.99c-.02-2.06 1.68-3.05 1.75-3.1-.96-1.4-2.45-1.59-2.97-1.61-1.26-.13-2.46.74-3.1.74-.65 0-1.64-.72-2.69-.7-1.38.02-2.66.8-3.37 2.04-1.44 2.5-.37 6.2 1.04 8.24.69.99 1.51 2.1 2.59 2.06 1.04-.04 1.44-.67 2.7-.67 1.26 0 1.61.67 2.71.65 1.12-.02 1.83-1.01 2.51-2 .79-1.15 1.12-2.27 1.13-2.33-.02-.01-2.17-.83-2.2-3.32zM9.62 3c.57-.69.96-1.65.86-2.6-.83.03-1.83.55-2.42 1.24-.53.6-.99 1.58-.87 2.51.93.07 1.87-.47 2.43-1.15z"/>
  </svg>
);

const btnGoogle: React.CSSProperties = {
  width: "100%",
  background: "#ffffff",
  border: "1px solid rgba(255,255,255,0.9)",
  borderRadius: 10,
  color: "#1f2937",
  fontWeight: 700,
  fontSize: "0.97rem",
  padding: "0.7rem",
  cursor: "pointer",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  gap: "0.6rem",
};

const dividerStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: "0.6rem",
  color: "rgba(148,163,184,0.65)",
  fontSize: "0.74rem",
  letterSpacing: "0.08em",
  textTransform: "uppercase",
};

const labelStyle: React.CSSProperties = {
  color: "rgba(255,255,255,0.7)",
  fontSize: "0.84rem",
};

const inlineErrorStyle: React.CSSProperties = {
  color: "#fca5a5",
  fontSize: "0.8rem",
  marginTop: "0.1rem",
};

const linkStyle: React.CSSProperties = {
  ...btnGhost,
  fontSize: "0.84rem",
};

const GoogleIcon = () => (
  <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true">
    <path fill="#4285F4" d="M17.64 9.2c0-.64-.06-1.25-.17-1.84H9v3.48h4.84a4.14 4.14 0 0 1-1.8 2.72v2.26h2.92c1.7-1.57 2.68-3.88 2.68-6.62z"/>
    <path fill="#34A853" d="M9 18c2.43 0 4.47-.8 5.96-2.18l-2.92-2.26c-.81.54-1.84.86-3.04.86-2.34 0-4.32-1.58-5.03-3.7H.97v2.32A9 9 0 0 0 9 18z"/>
    <path fill="#FBBC05" d="M3.97 10.72A5.4 5.4 0 0 1 3.68 9c0-.6.1-1.18.29-1.72V4.96H.97A9 9 0 0 0 0 9c0 1.45.35 2.83.97 4.04l3-2.32z"/>
    <path fill="#EA4335" d="M9 3.58c1.32 0 2.5.45 3.44 1.35l2.58-2.58A9 9 0 0 0 9 0 9 9 0 0 0 .97 4.96l3 2.32C4.68 5.16 6.66 3.58 9 3.58z"/>
  </svg>
);

const Spinner = () => (
  <span
    aria-hidden="true"
    style={{
      display: "inline-block",
      width: "1rem",
      height: "1rem",
      border: "2px solid rgba(255,255,255,0.4)",
      borderTopColor: "#ffffff",
      borderRadius: "50%",
      animation: "wp-login-spin 0.7s linear infinite",
      verticalAlign: "-3px",
    }}
  />
);

const spinnerKeyframes = `@keyframes wp-login-spin { to { transform: rotate(360deg); } }`;

function isValidEmail(email: string) {
  return /^\S+@\S+\.\S+$/.test(email.trim());
}

function readSavedEmail(): string {
  try {
    return window.localStorage.getItem(LAST_EMAIL_KEY) ?? "";
  } catch {
    return "";
  }
}

function writeSavedEmail(email: string) {
  try {
    window.localStorage.setItem(LAST_EMAIL_KEY, email);
  } catch {
    /* ignore */
  }
}

function parseRateLimitSeconds(message: string): number | null {
  const m = message.match(/(\d+)\s*(?:seconds|secs?|s)\b/i);
  if (m) return Math.min(300, parseInt(m[1], 10));
  if (/rate.?limit|too many|try again/i.test(message)) return 30;
  return null;
}

function authErrorBanner(message: string): Banner {
  const msg = message.toLowerCase();
  if (msg.includes("invalid email")) {
    return {
      variant: "error",
      title: "Invalid email",
      message: "Please enter a valid email address.",
    };
  }
  return {
    variant: "error",
    title: "Sign in failed",
    message: message || "Something went wrong. Please try again.",
  };
}

function bannerStyle(variant: Banner["variant"]): React.CSSProperties {
  if (variant === "success") {
    return {
      background: "rgba(34,197,94,0.1)",
      border: "1px solid rgba(34,197,94,0.3)",
      color: "#86efac",
    };
  }
  if (variant === "info") {
    return {
      background: "rgba(132,204,22,0.10)",
      border: "1px solid rgba(132,204,22,0.30)",
      color: "#d9f99d",
    };
  }
  return {
    background: "rgba(239,68,68,0.1)",
    border: `1px solid rgba(239,68,68,0.3)`,
    color: "#fca5a5",
  };
}

export function LoginModal({ onClose, initialMode = "signin", initialBanner = null, initialEmail = null, onPasswordUpdateSuccess }: Props) {
  const [mode, setMode] = useState<AuthModalMode>(initialMode);
  const [signinStep, setSigninStep] = useState<SignInStep>("identify");
  const [authMethod, setAuthMethod] = useState<AuthMethod>("magic");
  const [email, setEmail] = useState(() => initialEmail ?? readSavedEmail());
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [banner, setBanner] = useState<Banner | null>(initialBanner);
  const [showPassword, setShowPassword] = useState(false);
  const [resetEmail, setResetEmail] = useState("");
  const [resetBusy, setResetBusy] = useState(false);
  const [nextPassword, setNextPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [updateBusy, setUpdateBusy] = useState(false);
  const [googleBusy, setGoogleBusy] = useState(false);
  const [appleBusy, setAppleBusy] = useState(false);
  const [magicBusy, setMagicBusy] = useState(false);
  const [emailError, setEmailError] = useState<string | null>(null);
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [rateLimitUntil, setRateLimitUntil] = useState<number | null>(null);
  const [now, setNow] = useState<number>(() => Date.now());
  const [resendAvailableAt, setResendAvailableAt] = useState<number | null>(null);

  const emailRef = useRef<HTMLInputElement | null>(null);
  const passwordRef = useRef<HTMLInputElement | null>(null);
  const liveRegionRef = useRef<HTMLDivElement | null>(null);
  const initialEmailMissing = !(initialEmail ?? "").trim();
  const fieldIdBase = useId();
  const emailId = `${fieldIdBase}-email`;
  const passwordId = `${fieldIdBase}-password`;
  const emailErrorId = `${fieldIdBase}-email-error`;
  const passwordErrorId = `${fieldIdBase}-password-error`;
  const formErrorId = `${fieldIdBase}-form-error`;

  const canDismiss = mode !== "updatePassword";

  useEffect(() => {
    setMode(initialMode);
  }, [initialMode]);

  useEffect(() => {
    if (initialEmail) setEmail(initialEmail);
  }, [initialEmail]);

  useEffect(() => {
    setBanner(initialBanner);
  }, [initialBanner]);

  useEffect(() => {
    // On Capacitor iOS, the overlay's own overflow-y: auto + the keyboard-aware
    // padding (below) handle scroll-lock. Locking body to position:fixed here
    // defeats Capacitor's Keyboard.resize="body" reflow and traps the Continue
    // button behind the keyboard.
    if (isNative) return;
    const originalOverflow = document.body.style.overflow;
    const originalPosition = document.body.style.position;
    const originalWidth = document.body.style.width;
    const originalTop = document.body.style.top;
    const scrollY = window.scrollY;
    document.body.style.overflow = "hidden";
    document.body.style.position = "fixed";
    document.body.style.width = "100%";
    document.body.style.top = `-${scrollY}px`;
    return () => {
      document.body.style.overflow = originalOverflow;
      document.body.style.position = originalPosition;
      document.body.style.width = originalWidth;
      document.body.style.top = originalTop;
      window.scrollTo({ top: scrollY, behavior: "auto" });
    };
  }, []);

  const [keyboardHeight, setKeyboardHeight] = useState(0);
  useEffect(() => {
    if (!isNative) return;
    let cleanup: (() => void) | null = null;
    void (async () => {
      try {
        const { Keyboard } = await import("@capacitor/keyboard");
        const show = await Keyboard.addListener("keyboardWillShow", (info) => {
          setKeyboardHeight(info.keyboardHeight);
        });
        const hide = await Keyboard.addListener("keyboardWillHide", () => {
          setKeyboardHeight(0);
        });
        cleanup = () => {
          void show.remove();
          void hide.remove();
        };
      } catch (err) {
        console.warn("[loginModal] keyboard listener attach failed", err);
      }
    })();
    return () => {
      cleanup?.();
    };
  }, []);

  const overlayRuntimeStyle = useMemo<React.CSSProperties>(
    () =>
      keyboardHeight > 0
        ? {
            ...overlayStyle,
            paddingBottom: `calc(env(safe-area-inset-bottom) + 24px + ${keyboardHeight}px)`,
          }
        : overlayStyle,
    [keyboardHeight],
  );

  useEffect(() => {
    if (rateLimitUntil == null && resendAvailableAt == null) return;
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [rateLimitUntil, resendAvailableAt]);

  const rateLimitRemaining = useMemo(() => {
    if (!rateLimitUntil) return 0;
    return Math.max(0, Math.ceil((rateLimitUntil - now) / 1000));
  }, [rateLimitUntil, now]);

  const resendRemaining = useMemo(() => {
    if (!resendAvailableAt) return 0;
    return Math.max(0, Math.ceil((resendAvailableAt - now) / 1000));
  }, [resendAvailableAt, now]);

  // Focus management for the two-step sign-in flow. Skip on any phone (native
  // OR mobile web) so the keyboard doesn't auto-open and cover the Apple /
  // Google buttons sitting above the email field.
  useEffect(() => {
    if (mode !== "signin") return;
    const skipAutoFocus = isNative || isRealMobilePhone();
    if (signinStep === "identify") {
      if (!skipAutoFocus) window.setTimeout(() => emailRef.current?.focus(), 0);
    } else if (signinStep === "authenticate") {
      if (authMethod === "password") {
        if (!skipAutoFocus) window.setTimeout(() => passwordRef.current?.focus(), 0);
        if (liveRegionRef.current) {
          liveRegionRef.current.textContent = `Enter your password for ${email}`;
        }
      } else if (liveRegionRef.current) {
        liveRegionRef.current.textContent = `We will send a sign-in link to ${email}`;
      }
    }
  }, [signinStep, mode, email, authMethod]);

  const goIdentify = () => {
    setSigninStep("identify");
    setAuthMethod("magic");
    setPassword("");
    setPasswordError(null);
    setBanner(null);
  };

  const goAuthenticate = () => {
    const trimmed = email.trim();
    setEmailError(null);
    setBanner(null);
    if (!trimmed) {
      setEmailError("Enter your email address.");
      return;
    }
    if (!isValidEmail(trimmed)) {
      setEmailError("Please enter a valid email address.");
      return;
    }
    writeSavedEmail(trimmed);
    setEmail(trimmed);
    setSigninStep("authenticate");
  };

  const handleSignInSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (signinStep === "identify") {
      goAuthenticate();
      return;
    }

    if (signinStep !== "authenticate") return;
    if (rateLimitRemaining > 0) return;

    if (authMethod === "magic") {
      await sendMagicLink();
      return;
    }

    setBanner(null);
    setPasswordError(null);

    if (!password) {
      setPasswordError("Enter your password.");
      return;
    }

    setBusy(true);
    try {
      clearCompletedPasswordRecoveryEmail();
      const { error: err } = await signIn(email.trim(), password);
      if (err) throw err;
      onClose();
    } catch (err: any) {
      const msg = err?.message ?? "";
      const seconds = parseRateLimitSeconds(msg);
      if (seconds && /rate.?limit|too many/i.test(msg)) {
        setRateLimitUntil(Date.now() + seconds * 1000);
        setBanner({
          variant: "error",
          title: "Too many attempts",
          message: `Try again in ${seconds} seconds.`,
        });
      } else {
        setBanner({
          variant: "error",
          title: "Sign in failed",
          message: "Email or password is incorrect.",
        });
      }
    } finally {
      setBusy(false);
    }
  };

  const handleSignUpSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBanner(null);

    if (!isValidEmail(email)) {
      setBanner({ variant: "error", title: "Invalid email", message: "Please enter a valid email address." });
      return;
    }

    setBusy(true);
    try {
      const { data, error: err } = await signUp(email, password);

      const signUpErrorMessage = err?.message ?? "";
      const signUpUser = data?.user;
      const signUpSession = data?.session;
      const identities = Array.isArray((signUpUser as any)?.identities) ? (signUpUser as any).identities : null;
      const hasNoKnownIdentities = Array.isArray(identities) && identities.length === 0;
      const duplicateByErrorText = /already registered|already exists|email.*taken/i.test(signUpErrorMessage);
      const duplicateByResponseShape = !signUpSession && !!signUpUser && hasNoKnownIdentities;

      if (duplicateByErrorText || duplicateByResponseShape) {
        setMode("signin");
        setSigninStep("authenticate");
        setPassword("");
        setBanner({
          variant: "info",
          title: "Account found",
          message: "This email already has an account. Please sign in to continue.",
        });
        return;
      }

      if (err) throw err;
      if (!data.session) {
        setMode("signin");
        setSigninStep("identify");
        setPassword("");
        setBanner({
          variant: "success",
          title: "Check your email",
          message: `We sent a verification link to ${email}. Open it to activate your account, then come back and sign in.`,
        });
        return;
      }
      onClose();
    } catch (err: any) {
      const msg = err?.message ?? "";
      if (/already registered|already exists|email.*taken/i.test(msg)) {
        setMode("signin");
        setSigninStep("authenticate");
        setPassword("");
        setBanner({
          variant: "info",
          title: "Account found",
          message: "This email already has an account. Please sign in to continue.",
        });
      } else {
        setBanner(authErrorBanner(msg));
      }
    } finally {
      setBusy(false);
    }
  };

  const submitResetPassword = async () => {
    const nextEmail = resetEmail.trim();
    if (!isValidEmail(nextEmail)) {
      setBanner({ variant: "error", title: "Invalid email", message: "Please enter a valid email address." });
      return;
    }

    setResetBusy(true);
    setBanner(null);
    try {
      const { error } = await resetPassword(nextEmail);
      if (error) throw error;
      setBanner({
        variant: "success",
        title: "Reset email sent",
        message: "Check your inbox for a password reset link.",
      });
    } catch (err: any) {
      setBanner(authErrorBanner(err?.message ?? ""));
    } finally {
      setResetBusy(false);
    }
  };

  const goToReset = () => {
    setResetEmail(email);
    setBanner(null);
    setMode("reset");
  };

  const backToSignIn = () => {
    setBanner(null);
    setMode("signin");
  };

  const handleGoogle = async () => {
    setBanner(null);
    setGoogleBusy(true);
    console.info("[loginModal] handleGoogle start");
    try {
      const { error } = await signInWithGoogle();
      console.info("[loginModal] handleGoogle resolved", { errorMessage: error?.message ?? null });
      if (error) throw error;
      console.info("[loginModal] handleGoogle calling onClose");
      onClose();
    } catch (err: any) {
      setBanner({
        variant: "error",
        title: "Google sign-in failed",
        message: err?.message ?? "Could not start Google sign-in. Please try again.",
      });
    } finally {
      setGoogleBusy(false);
    }
  };

  const handleApple = async () => {
    setBanner(null);
    setAppleBusy(true);
    try {
      const { error } = await signInWithApple();
      if (error) throw error;
      onClose();
    } catch (err: any) {
      setBanner({
        variant: "error",
        title: "Apple sign-in failed",
        message: err?.message ?? "Could not start Apple sign-in. Please try again.",
      });
    } finally {
      setAppleBusy(false);
    }
  };

  const sendMagicLink = async () => {
    setBanner(null);
    const trimmed = email.trim();
    if (!isValidEmail(trimmed)) {
      setEmailError("Please enter a valid email address.");
      return;
    }
    setMagicBusy(true);
    try {
      const { error } = await signInWithMagicLink(trimmed);
      if (error) throw error;
      writeSavedEmail(trimmed);
      setSigninStep("magicSent");
      setResendAvailableAt(Date.now() + RESEND_SECONDS * 1000);
    } catch (err: any) {
      const msg = err?.message ?? "";
      const seconds = parseRateLimitSeconds(msg);
      if (seconds) {
        setRateLimitUntil(Date.now() + seconds * 1000);
        setBanner({
          variant: "error",
          title: "Too many attempts",
          message: `Try again in ${seconds} seconds.`,
        });
      } else {
        setBanner(authErrorBanner(msg));
      }
    } finally {
      setMagicBusy(false);
    }
  };

  const submitUpdatedPassword = async () => {
    if (!nextPassword || !confirmPassword) {
      setBanner({
        variant: "error",
        title: "Enter your new password",
        message: "Fill out both password fields to continue.",
      });
      return;
    }

    if (nextPassword.length < 8) {
      setBanner({
        variant: "error",
        title: "Password too short",
        message: "Use at least 8 characters for your new password.",
      });
      return;
    }

    if (nextPassword !== confirmPassword) {
      setBanner({
        variant: "error",
        title: "Passwords do not match",
        message: "Re-enter the same password in both fields.",
      });
      return;
    }

    setUpdateBusy(true);
    setBanner(null);
    try {
      const { error } = await updatePassword(nextPassword);
      if (error) throw error;
      if (onPasswordUpdateSuccess) {
        await onPasswordUpdateSuccess(initialEmail ?? email ?? null);
      } else {
        await signOut();
      }
    } catch (err: any) {
      setBanner({
        variant: "error",
        title: "Could not update password",
        message: err?.message ?? "Something went wrong. Please request a new reset link and try again.",
      });
    } finally {
      setUpdateBusy(false);
    }
  };

  // ── Reset password modal ──────────────────────────────────────────────────
  if (mode === "reset") {
    return (
      <div style={overlayRuntimeStyle} onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
        <style>{spinnerKeyframes}</style>
        <div style={panelStyle}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <div style={{ fontSize: "1.2rem", fontWeight: 900, color: "white" }}>Reset password</div>
            <button aria-label="Close sign in" style={{ ...btnGhost, display: "inline-flex", alignItems: "center", justifyContent: "center" }} onClick={onClose}><svg viewBox="0 0 10 10" width="12" height="12" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" aria-hidden="true"><line x1="1" y1="1" x2="9" y2="9"/><line x1="9" y1="1" x2="1" y2="9"/></svg></button>
          </div>

          <div style={{ color: "rgba(148,163,184,0.85)", fontSize: "0.85rem" }}>
            Enter your email and we’ll send you a link to set a new password.
          </div>

          <div style={{ display: "grid", gap: "0.75rem" }}>
            <div style={{ display: "grid", gap: "0.3rem" }}>
              <label htmlFor={`${fieldIdBase}-reset-email`} style={labelStyle}>Email</label>
              <input
                id={`${fieldIdBase}-reset-email`}
                style={inputStyle}
                type="email"
                autoComplete="email"
                value={resetEmail}
                onChange={(e) => setResetEmail(e.target.value)}
                placeholder="you@example.com"
                autoFocus={!isNative}
              />
            </div>

            {banner && (
              <div role="alert" style={{ ...bannerStyle(banner.variant), borderRadius: 8, padding: "0.5rem 0.7rem", fontSize: "0.84rem", display: "grid", gap: "0.2rem" }}>
                <div style={{ fontWeight: 800 }}>{banner.title}</div>
                <div>{banner.message}</div>
              </div>
            )}

            <button style={{ ...btnPrimary, opacity: resetBusy ? 0.7 : 1 }} type="button" disabled={resetBusy} onClick={submitResetPassword}>
              {resetBusy ? <><Spinner /> Sending…</> : "Send reset email"}
            </button>
          </div>

          <div style={{ textAlign: "center", fontSize: "0.84rem" }}>
            <button style={btnGhost} onClick={backToSignIn}>← Back to sign in</button>
          </div>
        </div>
      </div>
    );
  }

  if (mode === "updatePassword") {
    return (
      <div style={overlayRuntimeStyle} onClick={(e) => { if (canDismiss && e.target === e.currentTarget) onClose(); }}>
        <style>{spinnerKeyframes}</style>
        <div style={panelStyle}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <div style={{ fontSize: "1.2rem", fontWeight: 900, color: "white" }}>Set new password</div>
          </div>

          <div style={{ color: "rgba(148,163,184,0.85)", fontSize: "0.85rem" }}>
            Choose a new password for your account to finish the recovery flow, then sign in again to continue.
          </div>

          <div style={{ display: "grid", gap: "0.75rem" }}>
            {banner && (
              <div role="alert" style={{ ...bannerStyle(banner.variant), borderRadius: 8, padding: "0.5rem 0.7rem", fontSize: "0.84rem", display: "grid", gap: "0.2rem" }}>
                <div style={{ fontWeight: 800 }}>{banner.title}</div>
                <div>{banner.message}</div>
              </div>
            )}

            <div style={{ display: "grid", gap: "0.3rem" }}>
              <label htmlFor={`${fieldIdBase}-new-password`} style={labelStyle}>New password</label>
              <input
                id={`${fieldIdBase}-new-password`}
                style={inputStyle}
                type="password"
                autoComplete="new-password"
                value={nextPassword}
                onChange={(e) => setNextPassword(e.target.value)}
                placeholder="At least 8 characters"
                minLength={8}
                required
                autoFocus={!isNative}
              />
            </div>

            <div style={{ display: "grid", gap: "0.3rem" }}>
              <label htmlFor={`${fieldIdBase}-confirm-password`} style={labelStyle}>Confirm new password</label>
              <input
                id={`${fieldIdBase}-confirm-password`}
                style={inputStyle}
                type="password"
                autoComplete="new-password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Re-enter your new password"
                minLength={8}
                required
              />
            </div>

            <button style={{ ...btnPrimary, opacity: updateBusy ? 0.7 : 1 }} type="button" disabled={updateBusy} onClick={submitUpdatedPassword}>
              {updateBusy ? <><Spinner /> Updating…</> : "Update password"}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── Sign-up modal (unchanged from previous design; see TODO) ──────────────
  if (mode === "signup") {
    return (
      <div style={overlayRuntimeStyle} onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
        <style>{spinnerKeyframes}</style>
        <div style={panelStyle}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <div style={{ fontSize: "1.2rem", fontWeight: 900, color: "white" }}>Create account</div>
            <button aria-label="Close sign in" style={{ ...btnGhost, display: "inline-flex", alignItems: "center", justifyContent: "center" }} onClick={onClose}><svg viewBox="0 0 10 10" width="12" height="12" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" aria-hidden="true"><line x1="1" y1="1" x2="9" y2="9"/><line x1="9" y1="1" x2="1" y2="9"/></svg></button>
          </div>

          <div style={{ color: "rgba(148,163,184,0.85)", fontSize: "0.85rem" }}>
            Create a free account to sync across all devices.
          </div>

          <button type="button" style={{ ...btnApple, opacity: appleBusy ? 0.7 : 1 }} disabled={appleBusy} onClick={handleApple}>
            <AppleIcon />
            {appleBusy ? "Opening Apple…" : "Sign up with Apple"}
          </button>

          <button type="button" style={{ ...btnGoogle, opacity: googleBusy ? 0.7 : 1 }} disabled={googleBusy} onClick={handleGoogle}>
            <GoogleIcon />
            {googleBusy ? "Opening Google…" : "Continue with Google"}
          </button>

          <div style={dividerStyle}>
            <div style={{ flex: 1, height: 1, background: "rgba(148,163,184,0.2)" }} />
            <span>or</span>
            <div style={{ flex: 1, height: 1, background: "rgba(148,163,184,0.2)" }} />
          </div>

          <form onSubmit={handleSignUpSubmit} style={{ display: "grid", gap: "0.75rem" }}>
            <div style={{ display: "grid", gap: "0.3rem" }}>
              <label htmlFor={emailId} style={labelStyle}>Email</label>
              <input
                id={emailId}
                style={inputStyle}
                type="email"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                required
                autoFocus={!isNative && !isRealMobilePhone()}
              />
            </div>

            <div style={{ display: "grid", gap: "0.3rem" }}>
              <label htmlFor={passwordId} style={labelStyle}>Password</label>
              <div style={{ position: "relative" }}>
                <input
                  id={passwordId}
                  ref={passwordRef}
                  style={{ ...inputStyle, paddingRight: "3.5rem" }}
                  type={showPassword ? "text" : "password"}
                  autoComplete="new-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Min. 6 characters"
                  required
                  minLength={6}
                />
                <button
                  type="button"
                  aria-label={showPassword ? "Hide password" : "Show password"}
                  aria-pressed={showPassword}
                  style={{
                    position: "absolute",
                    right: 10,
                    top: "50%",
                    transform: "translateY(-50%)",
                    background: "none",
                    border: "none",
                    color: "rgba(148,163,184,0.9)",
                    fontSize: "0.78rem",
                    fontWeight: 700,
                    cursor: "pointer",
                    padding: 0,
                  }}
                  onClick={() => setShowPassword((v) => !v)}
                >
                  {showPassword ? "Hide" : "Show"}
                </button>
              </div>
            </div>

            {banner && (
              <div role="alert" style={{ ...bannerStyle(banner.variant), borderRadius: 8, padding: "0.5rem 0.7rem", fontSize: "0.84rem", display: "grid", gap: "0.2rem" }}>
                <div style={{ fontWeight: 800 }}>{banner.title}</div>
                <div>{banner.message}</div>
              </div>
            )}

            <button style={{ ...btnPrimary, opacity: busy ? 0.7 : 1 }} type="submit" disabled={busy}>
              {busy ? <><Spinner /> Please wait…</> : "Create account"}
            </button>
          </form>

          <div style={{ textAlign: "center", fontSize: "0.84rem", color: "rgba(148,163,184,0.7)" }}>
            Already have an account?{" "}
            <button style={btnGhost} onClick={() => { setMode("signin"); setSigninStep("identify"); setBanner(null); setPassword(""); }}>
              Sign in
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── Sign in modal (two-step progressive disclosure) ───────────────────────
  // TODO(future): SSO domain auto-routing (detect enterprise email → IdP).
  // TODO(future): persist preferred_auth_method per user.
  const showBack = signinStep !== "identify";
  const handleBack = () => {
    setBanner(null);
    setPasswordError(null);
    setPassword("");
    setSigninStep("identify");
  };
  const headerNode = (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "0.5rem" }}>
      <div style={{ display: "inline-flex", alignItems: "center", gap: "0.5rem", minWidth: 0 }}>
        {showBack && (
          <button
            type="button"
            aria-label="Back to sign-in options"
            onClick={handleBack}
            style={{
              ...btnGhost,
              textDecoration: "none",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              color: "rgba(226,232,240,0.9)",
            }}
          >
            <svg viewBox="0 0 16 16" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <polyline points="10 13 5 8 10 3" />
            </svg>
          </button>
        )}
        <div style={{ fontSize: "1.2rem", fontWeight: 900, color: "white" }}>Sign in</div>
      </div>
      <button aria-label="Close sign in" style={{ ...btnGhost, display: "inline-flex", alignItems: "center", justifyContent: "center" }} onClick={onClose}>
        <svg viewBox="0 0 10 10" width="12" height="12" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" aria-hidden="true"><line x1="1" y1="1" x2="9" y2="9"/><line x1="9" y1="1" x2="1" y2="9"/></svg>
      </button>
    </div>
  );

  // Magic link sent confirmation
  if (signinStep === "magicSent") {
    return (
      <div style={overlayRuntimeStyle} onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
        <style>{spinnerKeyframes}</style>
        <div style={panelStyle}>
          {headerNode}
          <div ref={liveRegionRef} aria-live="polite" style={{ position: "absolute", width: 1, height: 1, overflow: "hidden", clip: "rect(0 0 0 0)" }} />

          <div style={{ ...bannerStyle("success"), borderRadius: 10, padding: "0.9rem 1rem", display: "grid", gap: "0.35rem" }}>
            <div style={{ fontWeight: 800 }}>Check your inbox</div>
            <div style={{ fontSize: "0.9rem" }}>We sent a link to <strong>{email}</strong>. Open it on this device to sign in.</div>
          </div>

          <button
            type="button"
            style={{ ...btnPrimary, opacity: (magicBusy || resendRemaining > 0) ? 0.6 : 1 }}
            disabled={magicBusy || resendRemaining > 0}
            onClick={sendMagicLink}
          >
            {magicBusy
              ? <><Spinner /> Sending…</>
              : resendRemaining > 0
                ? `Resend in ${resendRemaining}s`
                : "Resend magic link"}
          </button>

          <div style={{ textAlign: "center" }}>
            <button type="button" style={linkStyle} onClick={() => { setSigninStep("authenticate"); setBanner(null); }}>
              Use password instead
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={overlayRuntimeStyle} onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <style>{spinnerKeyframes}</style>
      <div style={panelStyle}>
        {headerNode}

        <div ref={liveRegionRef} aria-live="polite" style={{ position: "absolute", width: 1, height: 1, overflow: "hidden", clip: "rect(0 0 0 0)" }} />

        <div style={{ color: "rgba(148,163,184,0.85)", fontSize: "0.85rem" }}>
          Sign in to sync your data across all devices.
        </div>

        {signinStep === "identify" && (
          <>
            <button
              type="button"
              style={{ ...btnApple, opacity: appleBusy ? 0.7 : 1 }}
              disabled={appleBusy}
              onClick={handleApple}
            >
              <AppleIcon />
              {appleBusy ? "Opening Apple…" : "Sign in with Apple"}
            </button>

            <button
              type="button"
              style={{ ...btnGoogle, opacity: googleBusy ? 0.7 : 1 }}
              disabled={googleBusy}
              onClick={handleGoogle}
            >
              <GoogleIcon />
              {googleBusy ? "Opening Google…" : "Continue with Google"}
            </button>

            <div style={dividerStyle}>
              <div style={{ flex: 1, height: 1, background: "rgba(148,163,184,0.2)" }} />
              <span>or</span>
              <div style={{ flex: 1, height: 1, background: "rgba(148,163,184,0.2)" }} />
            </div>
          </>
        )}

        {/* One logical form across both steps so password managers see email + password as a single sign-in form. */}
        <form onSubmit={handleSignInSubmit} style={{ display: "grid", gap: "0.75rem" }}>
          <div style={{ display: "grid", gap: "0.3rem" }}>
            <label htmlFor={emailId} style={labelStyle}>Email</label>
            {signinStep === "identify" ? (
              <>
                <input
                  id={emailId}
                  ref={emailRef}
                  style={emailError ? inputErrorStyle : inputStyle}
                  type="email"
                  autoComplete="email"
                  inputMode="email"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    if (emailError) setEmailError(null);
                    if (banner) setBanner(null);
                  }}
                  placeholder="you@example.com"
                  aria-invalid={emailError ? true : undefined}
                  aria-describedby={emailError ? emailErrorId : undefined}
                  autoFocus={!isNative && !isRealMobilePhone() && initialEmailMissing}
                  required
                />
                {emailError && (
                  <div id={emailErrorId} role="alert" style={inlineErrorStyle}>{emailError}</div>
                )}
              </>
            ) : (
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "0.5rem" }}>
                <div style={{ color: "white", fontSize: "0.95rem", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{email}</div>
                <button type="button" style={linkStyle} onClick={goIdentify}>Edit</button>
                {/* Hidden but real input keeps password managers happy on step 2. */}
                <input
                  type="email"
                  name="email"
                  autoComplete="email"
                  value={email}
                  readOnly
                  tabIndex={-1}
                  aria-hidden="true"
                  style={{ position: "absolute", width: 1, height: 1, padding: 0, margin: -1, overflow: "hidden", clip: "rect(0 0 0 0)", border: 0 }}
                />
              </div>
            )}
          </div>

          {signinStep === "authenticate" && authMethod === "magic" && (
            <div style={{ color: "rgba(148,163,184,0.85)", fontSize: "0.84rem" }}>
              We’ll email you a one-tap sign-in link. No password needed.
            </div>
          )}

          {signinStep === "authenticate" && authMethod === "password" && (
            <div style={{ display: "grid", gap: "0.3rem" }}>
              <label htmlFor={passwordId} style={labelStyle}>Password</label>
              <div style={{ position: "relative" }}>
                <input
                  id={passwordId}
                  ref={passwordRef}
                  style={{ ...(passwordError ? inputErrorStyle : inputStyle), paddingRight: "3.5rem" }}
                  type={showPassword ? "text" : "password"}
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => {
                    setPassword(e.target.value);
                    if (passwordError) setPasswordError(null);
                  }}
                  placeholder="Your password"
                  aria-invalid={passwordError ? true : undefined}
                  aria-describedby={passwordError ? passwordErrorId : undefined}
                  required
                  autoFocus={!isNative}
                />
                <button
                  type="button"
                  aria-label={showPassword ? "Hide password" : "Show password"}
                  aria-pressed={showPassword}
                  style={{
                    position: "absolute",
                    right: 10,
                    top: "50%",
                    transform: "translateY(-50%)",
                    background: "none",
                    border: "none",
                    color: "rgba(148,163,184,0.9)",
                    fontSize: "0.78rem",
                    fontWeight: 700,
                    cursor: "pointer",
                    padding: 0,
                  }}
                  onClick={() => setShowPassword((v) => !v)}
                >
                  {showPassword ? "Hide" : "Show"}
                </button>
              </div>
              {passwordError && (
                <div id={passwordErrorId} role="alert" style={inlineErrorStyle}>{passwordError}</div>
              )}
              <div>
                <button type="button" style={linkStyle} onClick={goToReset}>Forgot password?</button>
              </div>
            </div>
          )}

          {banner && (
            <div id={formErrorId} role="alert" style={{ ...bannerStyle(banner.variant), borderRadius: 8, padding: "0.5rem 0.7rem", fontSize: "0.84rem", display: "grid", gap: "0.2rem" }}>
              <div style={{ fontWeight: 800 }}>{banner.title}</div>
              <div>{banner.message}</div>
            </div>
          )}

          <button
            type="submit"
            style={{ ...btnPrimary, opacity: (busy || magicBusy || rateLimitRemaining > 0) ? 0.7 : 1 }}
            disabled={busy || magicBusy || rateLimitRemaining > 0}
          >
            {(() => {
              if (rateLimitRemaining > 0) return `Try again in ${rateLimitRemaining}s`;
              if (signinStep === "identify") {
                return busy ? <><Spinner /> Please wait…</> : "Continue";
              }
              if (authMethod === "magic") {
                return magicBusy ? <><Spinner /> Sending link…</> : "Email me a sign-in link";
              }
              return busy ? <><Spinner /> Signing in…</> : "Sign in";
            })()}
          </button>

          {signinStep === "authenticate" && (
            <div style={{ textAlign: "center" }}>
              <button
                type="button"
                style={linkStyle}
                onClick={() => {
                  setBanner(null);
                  setPasswordError(null);
                  setAuthMethod((m) => (m === "magic" ? "password" : "magic"));
                }}
              >
                {authMethod === "magic" ? "Or sign in with password" : "Email me a sign-in link instead"}
              </button>
            </div>
          )}
        </form>

        <div style={{ textAlign: "center", fontSize: "0.84rem", color: "rgba(148,163,184,0.7)" }}>
          Don’t have an account?{" "}
          <button style={btnGhost} onClick={() => { setMode("signup"); setBanner(null); setPassword(""); }}>
            Create one
          </button>
        </div>
      </div>
    </div>
  );
}
